import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { fromJS } from 'immutable';
import validator from 'validator';
import { uniqueId } from 'lodash';
import ReactResizeDetector from 'react-resize-detector';
import * as _ from 'lodash';

import injectReducer from 'utils/injectReducer';
import { removeTimeZoneFromDate } from 'utils/dateUtil';
import { makeSelectSportsEquipmentStep, makeSelectSportsEquipmentStepErrors } from './selectors';
import reducer from './reducer';
import messages from './messages';

import { Button } from '../components/Button';
import PageSectionHeader from '../components/PageSectionHeader';
import FlexBox from '../../../components/FlexBox';
import {
  ErrorMessage,
  Validate,
  ValidateGroup,
} from '../../../components/Validation';
import {
  handleFormDataChange,
  nextSlide,
  resetDamagedLostExpenseFormData,
  resetDelayedExpenseFormData,
  resetFormCompensationExpense,
} from './actions';
import DatePicker from '../components/DatePicker/index';
import TimePickerInput from '../../../components/TimePickerInput';
import Textbox from '../components/Textbox';
import TextArea from '../components/TextArea';
import OptionsContainer from '../components/OptionsContainer';
import { CLAIMING_FOR, REPORT_TO } from './constants';
import { AdditionalList } from './components/Additional';
import { required } from '../../../utils/validation/validator';
import { saveFormData } from '../actions';
import groupOption from '../../../components/Validation/groupOption';
import { makeSelectCurrentStep } from '../../Wizard/selectors';
import AsyncButton from '../../../components/AsyncButton/index';
import { makeSelectProgressBarStatus } from '../../ProgressBar/selectors';
import HelpText from '../../../components/HelpText';
import { stepPersonalItemsExpenseLookup } from '../../FastCover/dataMapper/claimCodeMapper';
import CurrencySelector from '../components/CurrencySelector';
import { stepSportsEquipmentLookup } from '../../FastCover/dataMapper/claimCodeMapper';


export class SportsEquipmentStep extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      validationCounter: 0,
      errors: [],
    };
    this.validatorGroup = new ValidateGroup();
    this.handleNext = this.handleNext.bind(this);
    this.checkFormError = this.checkFormError.bind(this);
    this.onResize = this.onResize.bind(this);

    this.saveButtonId = uniqueId();
    this.clickedItemId = '';
    this.nextButton = null;
  }

  componentWillReceiveProps(nextProps) {
    const { currentStep, step, stepName } = this.props;
    if ((stepName && nextProps.validateStep === stepName) || (currentStep !== nextProps.currentStep && currentStep === step)) {
      this.checkFormError();
    }

    if (this.props.isReadOnly && nextProps.errors !== this.props.errors) {
      this.state.validationCounter += 1;
      this.validatorGroup.checkValidation();
    }

    if (!nextProps.loading) {
      this.clickedItemId = '';
    }
  }

  onChange = (propertyName) => (event) => {
    if (event === null) {
      this.props.onFormDataChange(propertyName, null);
      return;
    }

    if (event.constructor.name === 'Moment') {
      this.props.onFormDataChange(propertyName, removeTimeZoneFromDate(event.toDate().toString()));
    } else {
      const target = event.target;
      const value = target.type === 'checkbox' ? target.checked : target.value;
      if (target.type === 'radio') {
        this.props.adjust();
      }
      this.props.onFormDataChange(propertyName, value);
    }
    // this.props.makeDirty(6);
  };

  onAdditionalItemsChange = (data) => {
    this.props.onFormDataChange('form.additionalLostSportsEquipmentRentalItems', fromJS(data));
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  onLostSportsEquipmentExpensesChange = (data) => {
    this.props.onFormDataChange('form.expenseItemsLostSportsEquipment', fromJS(data));
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  onSportsEquipmentDelayExpensesChange = (data) => {
    this.props.onFormDataChange('form.expenseItemsSportsEquipmentDelay', fromJS(data));
    const { adjust } = this.props;
    if (adjust) adjust();
  };

  onInputComponentChange = (propertyName) => (event) => {
    if (event.target.checked) {
      const selectedValue = this.getSelectedClaiming();
      if (!!selectedValue && selectedValue !== propertyName) {
        this.props.onFormDataChange(`form.${selectedValue}`, 'false');
      }
    }
    this.onChange(`form.${propertyName}`)(event);
    // this.props.makeDirty(6);
  };

  onResize = () => {
    const { adjust } = this.props;
    if (adjust) {
      adjust();
    }
  };

  getSelectedClaiming = () => {
    const {
      claimForPassportCreditCard,
      claimForTheftOfCash,
      claimForLuggageAndPersonalEffect,
    } = this.props.form;

    let selectedValue = null;

    switch ('true') {
      case claimForPassportCreditCard:
        selectedValue = CLAIMING_FOR.PASSPORT_CREDIT_CARD;
        break;
      case claimForTheftOfCash:
        selectedValue = CLAIMING_FOR.THEFT_OF_CASH;
        break;
      case claimForLuggageAndPersonalEffect:
        selectedValue = CLAIMING_FOR.LUGGAGE_AND_PERSONAL_EFFECTS;
        break;
      default:
        selectedValue = null;
        break;
    }

    return selectedValue;
  };

  getValidationErrors() {
    const {
      areYouClaimingForDamaged,
      areYouClaimingForBaggageDelay,
      additionalItems,
      delayedLuggageExpenses,
    } = this.props.form;
    const errors = [];

    if (areYouClaimingForDamaged === 'true') {
      if (this.validateTableField(additionalItems)) {
        errors.push({
          field: 'additionalItems',
          message: 'Expense table fields are mandatory fields.',
        });
      }
    }

    if (areYouClaimingForBaggageDelay === 'true') {
      if (this.validateTableField(delayedLuggageExpenses)) {
        errors.push({
          field: 'delayedLuggageExpenses',
          message: 'Delayed Luggage Expenses table fields are required fields.',
        });
      }
    }

    return errors;
  }

  shouldDisable = (field) => {
    const selectedValue = this.getSelectedClaiming();

    return !!selectedValue && selectedValue !== field;
  };

  appendIds = (array) =>
    array.map((item) => {
      let newItem = item; // eslint-disable-line
      if (!item.id) {
        newItem.id = new Date().getTime().toString();
      }
      return newItem;
    });

  validateTableField(groups) {
    const invalid = _.some(groups, (fields) =>
      _.some(fields, (value) => {
        const newValue = value ? value.toString() : '';
        return validator.isEmpty(newValue);
      })
    );
    return invalid;
  }

  handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Tab') {
      return;
    }
    if (e.key === 'Tab') {
      e.preventDefault();
    }
  };

  handleSaveButtonClick = (event) => {
    const target = event.currentTarget || event.srcElement;
    this.clickedItemId = target.id;
    this.props.onFormDataSave();
    this.nextButton.focus();
  };

  showSpinnerForElement = (elementId) => this.clickedItemId === elementId;

  checkFormError() {
    const valid = this.validatorGroup.checkValidation();
    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      const counter = this.state.validationCounter + 1;
      this.setState({ validationCounter: counter });
    } else {
      this.props.makeValid(this.props.step);
    }
  }

  handleChange = (propertyName) => (event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    this.props.onFormDataChange(propertyName, value);
  };

  handleNext = () => {
    const { submitted } = this.props;
    if (submitted) {
      this.props.next();
      return;
    }

    // const errors = this.validatorGroup.getErrors();
    const valid = this.validatorGroup.checkValidation();

    this.props.onNext(!valid);
    if (!valid) {
      this.props.makeError(this.props.step);
      const counter = this.state.validationCounter + 1;
      this.setState({ validationCounter: counter });
    } else {
      this.props.makeValid(this.props.step);
    }

    this.props.onFormDataSave();
    this.props.next();
  };

  handleFormDataSave = () => {
    this.props.onFormDataSave();
  };

  handleDayChange = (propertyName) => (day) => {
    const dayWithoutTimeZone = removeTimeZoneFromDate(day);
    this.props.onFormDataChange(propertyName, dayWithoutTimeZone);
    // this.props.makeDirty(6);
  };

  summaryFormValue() {
    return _.mapValues(this.props.form, (prop) => prop || 'N/A');
  }

  // Are you claiming for Lost Sports Equipment Rental?
  renderClaimingForLostSportsEquipmentRental() {
    const {
      areYouClaimingForLostSportsEquipmentRental,
    } = this.props.form;
    return (
      areYouClaimingForLostSportsEquipmentRental === 'true' &&
      this.renderClaimingForLostSportsEquipmentRentalForm()
    );
  }

  renderClaimingForLostSportsEquipmentRentalForm() {
    const { isSummary, submitted } = this.props;

    const {
      dateOfLossForLostSportsEquipmentRentalInfo,
      placeOfLossForLostSportsEquipmentRentalInfo,
      addressForLostSportsEquipmentRentalInfo,
      contactForLostSportsEquipmentRentalInfo,
      phoneForLostSportsEquipmentRentalInfo,
      pleaseDescribeWhatOccurredForLostSportsEquipmentRentalInfo,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;
    return (
      // selectedClaiming &&
      <div className="mb-4 ml-4">
        {this.renderTravelSupplierForLostSportsEquipmentRental()}
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.lossInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
          <div className="form-group col-md-6">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="dateOfLossForLostSportsEquipmentRentalInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={dateOfLossForLostSportsEquipmentRentalInfo}
                fieldName={messages.dateOfLoss}
              >
                <DatePicker
                  position={'bottom-start'}
                  disabled={isReadOnly}
                  name="dateOfLossForLostSportsEquipmentRentalInfo"
                  type="text"
                  onDayChange={this.handleDayChange('form.dateOfLossForLostSportsEquipmentRentalInfo')}
                  value={dateOfLossForLostSportsEquipmentRentalInfo}
                  placeholder=""
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.dateOfLoss}
                  />
                </ErrorMessage>
              </Validate>
          </div>
            <div className="form-group col-sm-6">
            </div>
        </div>       
        <div className="row">
        <div className="form-group col-sm-12">
          <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="placeOfLossForLostSportsEquipmentRentalInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={pleaseDescribeWhatOccurredForLostSportsEquipmentRentalInfo}
                fieldName={messages.pleaseDescribeWhatOccurred}
              >
                <TextArea
                  disabled={isReadOnly}
                  name="pleaseDescribeWhatOccurred"
                  type="text"
                  onChange={this.handleChange('form.pleaseDescribeWhatOccurredForLostSportsEquipmentRentalInfo')}
                  value={pleaseDescribeWhatOccurredForLostSportsEquipmentRentalInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.pleaseDescribeWhatOccurred}
                  />
                </ErrorMessage>
              </Validate>
              </div>
        </div>
        <div className={'row'}>
            <div className="form-group col-sm-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="placeOfLossForLostSportsEquipmentRentalInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={placeOfLossForLostSportsEquipmentRentalInfo}
                fieldName={messages.placeOfLoss}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="placeOfLossForLostSportsEquipmentRentalInfo"
                  type="text"
                  onChange={this.handleChange('form.placeOfLossForLostSportsEquipmentRentalInfo')}
                  value={placeOfLossForLostSportsEquipmentRentalInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.placeOfLoss}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            
        </div>
        <div className={'row'}>
        <div className="form-group col-sm-12">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="addressForLostSportsEquipmentRentalInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={addressForLostSportsEquipmentRentalInfo}
                fieldName={messages.address}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="addressForLostSportsEquipmentRentalInfo"
                  type="text"
                  onChange={this.handleChange('form.addressForLostSportsEquipmentRentalInfo')}
                  value={addressForLostSportsEquipmentRentalInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.address}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        <div className={'row'}>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="contactForLostSportsEquipmentRentalInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={contactForLostSportsEquipmentRentalInfo}
                fieldName={messages.contact}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="contactForLostSportsEquipmentRentalInfo"
                  type="text"
                  onChange={this.handleChange('form.contactForLostSportsEquipmentRentalInfo')}
                  value={contactForLostSportsEquipmentRentalInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.contact}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="phoneForLostSportsEquipmentRentalInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={phoneForLostSportsEquipmentRentalInfo}
                fieldName={messages.phone}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="phoneForLostSportsEquipmentRentalInfo"
                  type="text"
                  onChange={this.handleChange('form.phoneForLostSportsEquipmentRentalInfo')}
                  value={phoneForLostSportsEquipmentRentalInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.phone}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        {this.renderAdditionalItems()}
        {this.renderOtherInsuranceAuthorizationForLostSportsEquipmentRental()}
      </div>
    );
  }

  renderTravelSupplierForLostSportsEquipmentRental() {
    const { isSummary, submitted } = this.props;
    const {
      originationForLostSportsEquipmentRental,
      flightNumberOriginationForLostSportsEquipmentRental,
      airCarrierOriginationForLostSportsEquipmentRental,
      destinationForLostSportsEquipmentRental,
      flightNumberDestinationForLostSportsEquipmentRental,
      airCarrierDestinationForLostSportsEquipmentRental,
      nameOfTourOperatorOrCruiseOrLineOrAirlineForLostSportsEquipmentRental,
      scheduledDateOfDepartureForLostSportsEquipmentRental,
      scheduledDateOfReturnForLostSportsEquipmentRental,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="ml-2">
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.travelSupplierOrProviderInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
            <div className="form-group col-sm-8">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="nameOfTourOperatorOrCruiseOrLineOrAirlineForLostSportsEquipmentRental"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={nameOfTourOperatorOrCruiseOrLineOrAirlineForLostSportsEquipmentRental}
                fieldName={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="nameOfTourOperatorOrCruiseOrLineOrAirlineForLostSportsEquipmentRental"
                  type="text"
                  onChange={this.handleChange('form.nameOfTourOperatorOrCruiseOrLineOrAirlineForLostSportsEquipmentRental')}
                  value={nameOfTourOperatorOrCruiseOrLineOrAirlineForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
            </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-md-6">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="scheduledDateOfDeparture"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={scheduledDateOfDepartureForLostSportsEquipmentRental}
                fieldName={messages.scheduledDateOfDeparture}
              >
                <DatePicker
                  position={'bottom-start'}
                  disabled={isReadOnly}
                  name="scheduledDateOfDeparture"
                  type="text"
                  onDayChange={this.handleDayChange('form.scheduledDateOfDepartureForLostSportsEquipmentRental')}
                  value={scheduledDateOfDepartureForLostSportsEquipmentRental}
                  placeholder=""
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.scheduledDateOfDeparture}
                  />
                </ErrorMessage>
              </Validate>
          </div>
          <div className="form-group col-md-6">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="scheduledDateOfReturn"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={scheduledDateOfReturnForLostSportsEquipmentRental}
                fieldName={messages.scheduledDateOfReturn}
              >
                <DatePicker
                  position={'bottom-start'}
                  disabled={isReadOnly}
                  name="scheduledDateOfReturn"
                  type="text"
                  onDayChange={this.handleDayChange('form.scheduledDateOfReturnForLostSportsEquipmentRental')}
                  value={scheduledDateOfReturnForLostSportsEquipmentRental}
                  placeholder=""
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.scheduledDateOfReturn}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>
        <div className="row">
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="origination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={originationForLostSportsEquipmentRental}
                fieldName={messages.origination}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="origination"
                  type="text"
                  onChange={this.handleChange('form.originationForLostSportsEquipmentRental')}
                  value={originationForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.origination}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="flightNumberOrigination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={flightNumberOriginationForLostSportsEquipmentRental}
                fieldName={messages.flightNumber}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="flightNumberOrigination"
                  type="text"
                  onChange={this.handleChange('form.flightNumberOriginationForLostSportsEquipmentRental')}
                  value={flightNumberOriginationForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.flightNumber}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="airCarrierOrigination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={airCarrierOriginationForLostSportsEquipmentRental}
                fieldName={messages.airCarrier}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="airCarrierOrigination"
                  type="text"
                  onChange={this.handleChange('form.airCarrierOriginationForLostSportsEquipmentRental')}
                  value={airCarrierOriginationForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.airCarrier}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        <div className="row">
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="destination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={destinationForLostSportsEquipmentRental}
                fieldName={messages.destination}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="destination"
                  type="text"
                  onChange={this.handleChange('form.destinationForLostSportsEquipmentRental')}
                  value={destinationForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.destination}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="flightNumberDestination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={flightNumberDestinationForLostSportsEquipmentRental}
                fieldName={messages.flightNumber}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="flightNumberDestination"
                  type="text"
                  onChange={this.handleChange('form.flightNumberDestinationForLostSportsEquipmentRental')}
                  value={flightNumberDestinationForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.flightNumber}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="airCarrierDestination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={airCarrierDestinationForLostSportsEquipmentRental}
                fieldName={messages.airCarrier}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="airCarrierDestination"
                  type="text"
                  onChange={this.handleChange('form.airCarrierDestinationForLostSportsEquipmentRental')}
                  value={airCarrierDestinationForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.airCarrier}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
    </div>
    );
  }

  renderOtherInsuranceAuthorizationForLostSportsEquipmentRental() {
    const { isSummary, submitted } = this.props;
    const {
      companyNameForLostSportsEquipmentRental,
      addressForLostSportsEquipmentRental,
      typeOfPolicyForLostSportsEquipmentRental,
      policyForLostSportsEquipmentRental,
      contactForLostSportsEquipmentRental,
      phoneForLostSportsEquipmentRental,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.otherInsuranceAuthorization} />
          </PageSectionHeader>
        </div>
        <div className="row">
            <div className="form-group col-sm-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="companyNameForLostSportsEquipmentRental"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={companyNameForLostSportsEquipmentRental}
                fieldName={messages.companyName}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="companyNameForLostSportsEquipmentRental"
                  type="text"
                  onChange={this.handleChange('form.companyNameForLostSportsEquipmentRental')}
                  value={companyNameForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.companyName}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="addressForLostSportsEquipmentRental"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={addressForLostSportsEquipmentRental}
                fieldName={messages.address}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="companyNameForLostSportsEquipmentRental"
                  type="text"
                  onChange={this.handleChange('form.addressForLostSportsEquipmentRental')}
                  value={addressForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.address}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        <div className="row">
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="typeOfPolicyForLostSportsEquipmentRental"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={typeOfPolicyForLostSportsEquipmentRental}
                fieldName={messages.typeOfPolicy}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="typeOfPolicyForLostSportsEquipmentRental"
                  type="text"
                  onChange={this.handleChange('form.typeOfPolicyForLostSportsEquipmentRental')}
                  value={typeOfPolicyForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.typeOfPolicy}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="policyForLostSportsEquipmentRental"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={policyForLostSportsEquipmentRental}
                fieldName={messages.policy}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="policyForLostSportsEquipmentRental"
                  type="text"
                  onChange={this.handleChange('form.policyForLostSportsEquipmentRental')}
                  value={policyForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.policy}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        <div className="row">
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="contactForLostSportsEquipmentRental"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={contactForLostSportsEquipmentRental}
                fieldName={messages.contact}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="contactForLostSportsEquipmentRental"
                  type="text"
                  onChange={this.handleChange('form.contactForLostSportsEquipmentRental')}
                  value={contactForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.contact}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="phoneForLostSportsEquipmentRental"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={phoneForLostSportsEquipmentRental}
                fieldName={messages.phone}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="phoneForLostSportsEquipmentRental"
                  type="text"
                  onChange={this.handleChange('form.phoneForLostSportsEquipmentRental')}
                  value={phoneForLostSportsEquipmentRental}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.phone}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
    </div>
    );
  }

  renderAdditionalItems() {
    const { additionalLostSportsEquipmentRentalItems } = this.props.form;
    const newAdditionalItems = this.appendIds(additionalLostSportsEquipmentRentalItems);
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <PageSectionHeader>
          <FormattedMessage {...messages.additionalItem} />
        </PageSectionHeader>
        <AdditionalList
          data={newAdditionalItems}
          onChange={this.onAdditionalItemsChange}
          isDelayed={false}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
        />
      </div>
    );
  }

  // Are you claiming for Lost Sports Equipment?
  renderClaimingForLostSportsEquipment() {
    const {
      areYouClaimingForLostSportsEquipment,
    } = this.props.form;
    return (
      areYouClaimingForLostSportsEquipment === 'true' &&
      this.renderClaimingForLostSportsEquipmentForm()
    );
  }

  renderClaimingForLostSportsEquipmentForm() {
    const { isSummary, submitted } = this.props;

    const {
      dateOfLossLostSportsEquipmentInfo,
      placeOfLossLostSportsEquipmentInfo,
      addressLostSportsEquipmentInfo,
      contactLostSportsEquipmentInfo,
      phoneLostSportsEquipmentInfo,
      pleaseDescribeWhatOccurredLostSportsEquipmentInfo,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;
    return (
      // selectedClaiming &&
      <div className="mb-4 ml-4">
        {this.renderTravelSupplierForLostSportsEquipment()}
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.lossInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
          <div className="form-group col-md-6">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="dateOfLossForBaggageInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={dateOfLossLostSportsEquipmentInfo}
                fieldName={messages.dateOfLoss}
              >
                <DatePicker
                  position={'bottom-start'}
                  disabled={isReadOnly}
                  name="dateOfLossForBaggageInfo"
                  type="text"
                  onDayChange={this.handleDayChange('form.dateOfLossLostSportsEquipmentInfo')}
                  value={dateOfLossLostSportsEquipmentInfo}
                  placeholder=""
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.dateOfLoss}
                  />
                </ErrorMessage>
              </Validate>
          </div>
            <div className="form-group col-sm-6">
            </div>
        </div>       
        <div className="row">
        <div className="form-group col-sm-12">
          <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="placeOfLossForBaggageInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={pleaseDescribeWhatOccurredLostSportsEquipmentInfo}
                fieldName={messages.pleaseDescribeWhatOccurred}
              >
                <TextArea
                  disabled={isReadOnly}
                  name="pleaseDescribeWhatOccurred"
                  type="text"
                  onChange={this.handleChange('form.pleaseDescribeWhatOccurredLostSportsEquipmentInfo')}
                  value={pleaseDescribeWhatOccurredLostSportsEquipmentInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.pleaseDescribeWhatOccurred}
                  />
                </ErrorMessage>
              </Validate>
              </div>
        </div>
        <div className={'row'}>
            <div className="form-group col-sm-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="placeOfLossForBaggageInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={placeOfLossLostSportsEquipmentInfo}
                fieldName={messages.placeOfLoss}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="placeOfLossForBaggageInfo"
                  type="text"
                  onChange={this.handleChange('form.placeOfLossLostSportsEquipmentInfo')}
                  value={placeOfLossLostSportsEquipmentInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.placeOfLoss}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            
        </div>
        <div className={'row'}>
        <div className="form-group col-sm-12">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="addressForBaggageInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={addressLostSportsEquipmentInfo}
                fieldName={messages.address}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="addressForBaggageInfo"
                  type="text"
                  onChange={this.handleChange('form.addressLostSportsEquipmentInfo')}
                  value={addressLostSportsEquipmentInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.address}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        <div className={'row'}>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="contactForBaggageInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={contactLostSportsEquipmentInfo}
                fieldName={messages.contact}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="contactForBaggageInfo"
                  type="text"
                  onChange={this.handleChange('form.contactLostSportsEquipmentInfo')}
                  value={contactLostSportsEquipmentInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.contact}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="phoneForBaggageInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={phoneLostSportsEquipmentInfo}
                fieldName={messages.phone}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="phoneForBaggageInfo"
                  type="text"
                  onChange={this.handleChange('form.phoneLostSportsEquipmentInfo')}
                  value={phoneLostSportsEquipmentInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.phone}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        {this.renderExpenseItemsLostSportsEquipment()}
        {this.renderOtherInsuranceAuthorizationForLostSportsEquipment()}
      </div>
    );
  }

  renderTravelSupplierForLostSportsEquipment() {
    const { isSummary, submitted } = this.props;
    const {
      originationLostSportsEquipment,
      flightNumberOriginationLostSportsEquipment,
      airCarrierOriginationLostSportsEquipment,
      destinationLostSportsEquipment,
      flightNumberDestinationLostSportsEquipment,
      airCarrierDestinationLostSportsEquipment,
      nameOfTourOperatorOrCruiseOrLineOrAirlineLostSportsEquipment,
      scheduledDateOfDepartureLostSportsEquipment,
      scheduledDateOfReturnLostSportsEquipment,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="ml-2">
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.travelSupplierOrProviderInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
            <div className="form-group col-sm-8">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={nameOfTourOperatorOrCruiseOrLineOrAirlineLostSportsEquipment}
                fieldName={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage"
                  type="text"
                  onChange={this.handleChange('form.nameOfTourOperatorOrCruiseOrLineOrAirlineLostSportsEquipment')}
                  value={nameOfTourOperatorOrCruiseOrLineOrAirlineLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
            </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-md-6">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="scheduledDateOfDeparture"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={scheduledDateOfDepartureLostSportsEquipment}
                fieldName={messages.scheduledDateOfDeparture}
              >
                <DatePicker
                  position={'bottom-start'}
                  disabled={isReadOnly}
                  name="scheduledDateOfDeparture"
                  type="text"
                  onDayChange={this.handleDayChange('form.scheduledDateOfDepartureLostSportsEquipment')}
                  value={scheduledDateOfDepartureLostSportsEquipment}
                  placeholder=""
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.scheduledDateOfDeparture}
                  />
                </ErrorMessage>
              </Validate>
          </div>
          <div className="form-group col-md-6">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="scheduledDateOfReturn"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={scheduledDateOfReturnLostSportsEquipment}
                fieldName={messages.scheduledDateOfReturn}
              >
                <DatePicker
                  position={'bottom-start'}
                  disabled={isReadOnly}
                  name="scheduledDateOfReturn"
                  type="text"
                  onDayChange={this.handleDayChange('form.scheduledDateOfReturnLostSportsEquipment')}
                  value={scheduledDateOfReturnLostSportsEquipment}
                  placeholder=""
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.scheduledDateOfReturn}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>
        <div className="row">
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="origination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={originationLostSportsEquipment}
                fieldName={messages.origination}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="origination"
                  type="text"
                  onChange={this.handleChange('form.originationLostSportsEquipment')}
                  value={originationLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.origination}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="flightNumberOrigination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={flightNumberOriginationLostSportsEquipment}
                fieldName={messages.flightNumber}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="flightNumberOrigination"
                  type="text"
                  onChange={this.handleChange('form.flightNumberOriginationLostSportsEquipment')}
                  value={flightNumberOriginationLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.flightNumber}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="airCarrierOrigination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={airCarrierOriginationLostSportsEquipment}
                fieldName={messages.airCarrier}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="airCarrierOrigination"
                  type="text"
                  onChange={this.handleChange('form.airCarrierOriginationLostSportsEquipment')}
                  value={airCarrierOriginationLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.airCarrier}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        <div className="row">
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="destination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={destinationLostSportsEquipment}
                fieldName={messages.destination}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="destination"
                  type="text"
                  onChange={this.handleChange('form.destinationLostSportsEquipment')}
                  value={destinationLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.destination}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="flightNumberDestination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={flightNumberDestinationLostSportsEquipment}
                fieldName={messages.flightNumber}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="flightNumberDestination"
                  type="text"
                  onChange={this.handleChange('form.flightNumberDestinationLostSportsEquipment')}
                  value={flightNumberDestinationLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.flightNumber}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="airCarrierDestination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={airCarrierDestinationLostSportsEquipment}
                fieldName={messages.airCarrier}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="airCarrierDestination"
                  type="text"
                  onChange={this.handleChange('form.airCarrierDestinationLostSportsEquipment')}
                  value={airCarrierDestinationLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.airCarrier}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
    </div>
    );
  }

  renderExpenseItemsLostSportsEquipment() {
    const { expenseItemsLostSportsEquipment } = this.props.form;
    const newAdditionalItems = this.appendIds(expenseItemsLostSportsEquipment);
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <PageSectionHeader>
          <FormattedMessage {...messages.additionalItem} />
        </PageSectionHeader>
        <AdditionalList
          data={newAdditionalItems}
          onChange={this.onLostSportsEquipmentExpensesChange}
          isDelayed={false}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
        />
      </div>
    );
  }

  renderOtherInsuranceAuthorizationForLostSportsEquipment() {
    const { isSummary, submitted } = this.props;
    const {
      companyNameLostSportsEquipment,
      addressLostSportsEquipment,
      typeOfPolicyLostSportsEquipment,
      policyLostSportsEquipment,
      contactLostSportsEquipment,
      phoneLostSportsEquipment,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.otherInsuranceAuthorization} />
          </PageSectionHeader>
        </div>
        <div className="row">
            <div className="form-group col-sm-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="companyNameForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={companyNameLostSportsEquipment}
                fieldName={messages.companyName}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="companyNameForBaggage"
                  type="text"
                  onChange={this.handleChange('form.companyNameLostSportsEquipment')}
                  value={companyNameLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.companyName}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="addressForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={addressLostSportsEquipment}
                fieldName={messages.address}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="companyNameForBaggage"
                  type="text"
                  onChange={this.handleChange('form.addressLostSportsEquipment')}
                  value={addressLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.address}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        <div className="row">
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="typeOfPolicyForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={typeOfPolicyLostSportsEquipment}
                fieldName={messages.typeOfPolicy}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="typeOfPolicyForBaggage"
                  type="text"
                  onChange={this.handleChange('form.typeOfPolicyLostSportsEquipment')}
                  value={typeOfPolicyLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.typeOfPolicy}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="policyForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={policyLostSportsEquipment}
                fieldName={messages.policy}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="policyForBaggage"
                  type="text"
                  onChange={this.handleChange('form.policyLostSportsEquipment')}
                  value={policyLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.policy}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        <div className="row">
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="contactForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={contactLostSportsEquipment}
                fieldName={messages.contact}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="contactForBaggage"
                  type="text"
                  onChange={this.handleChange('form.contactLostSportsEquipment')}
                  value={contactLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.contact}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="phoneForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={phoneLostSportsEquipment}
                fieldName={messages.phone}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="phoneForBaggage"
                  type="text"
                  onChange={this.handleChange('form.phoneLostSportsEquipment')}
                  value={phoneLostSportsEquipment}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.phone}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
    </div>
    );
  }

  // Are you claiming for Sports Equipment Delay?
  renderClaimingForSportsEquipmentDelay() {
    const {
      areYouClaimingForSportsEquipmentDelay,
    } = this.props.form;
    return (
      areYouClaimingForSportsEquipmentDelay === 'true' &&
      this.renderClaimingForSportsEquipmentDelayForm()
    );
  }

  renderClaimingForSportsEquipmentDelayForm() {
    const { isSummary, submitted } = this.props;

    const {
      dateOfLossSportsEquipmentDelayInfo,
      placeOfLossSportsEquipmentDelayInfo,
      addressSportsEquipmentDelayInfo,
      contactSportsEquipmentDelayInfo,
      phoneSportsEquipmentDelayInfo,
      pleaseDescribeWhatOccurredSportsEquipmentDelayInfo,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;
    return (
      // selectedClaiming &&
      <div className="mb-4 ml-4">
        {this.renderTravelSupplierForSportsEquipmentDelay()}
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.lossInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
          <div className="form-group col-md-6">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="dateOfLossForBaggageInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={dateOfLossSportsEquipmentDelayInfo}
                fieldName={messages.dateOfLoss}
              >
                <DatePicker
                  position={'bottom-start'}
                  disabled={isReadOnly}
                  name="dateOfLossForBaggageInfo"
                  type="text"
                  onDayChange={this.handleDayChange('form.dateOfLossSportsEquipmentDelayInfo')}
                  value={dateOfLossSportsEquipmentDelayInfo}
                  placeholder=""
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.dateOfLoss}
                  />
                </ErrorMessage>
              </Validate>
          </div>
            <div className="form-group col-sm-6">
            </div>
        </div>       
        <div className="row">
        <div className="form-group col-sm-12">
          <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="placeOfLossForBaggageInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={pleaseDescribeWhatOccurredSportsEquipmentDelayInfo}
                fieldName={messages.pleaseDescribeWhatOccurred}
              >
                <TextArea
                  disabled={isReadOnly}
                  name="pleaseDescribeWhatOccurred"
                  type="text"
                  onChange={this.handleChange('form.pleaseDescribeWhatOccurredSportsEquipmentDelayInfo')}
                  value={pleaseDescribeWhatOccurredSportsEquipmentDelayInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.pleaseDescribeWhatOccurred}
                  />
                </ErrorMessage>
              </Validate>
              </div>
        </div>
        <div className={'row'}>
            <div className="form-group col-sm-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="placeOfLossForBaggageInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={placeOfLossSportsEquipmentDelayInfo}
                fieldName={messages.placeOfLoss}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="placeOfLossForBaggageInfo"
                  type="text"
                  onChange={this.handleChange('form.placeOfLossSportsEquipmentDelayInfo')}
                  value={placeOfLossSportsEquipmentDelayInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.placeOfLoss}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            
        </div>
        <div className={'row'}>
        <div className="form-group col-sm-12">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="addressForBaggageInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={addressSportsEquipmentDelayInfo}
                fieldName={messages.address}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="addressForBaggageInfo"
                  type="text"
                  onChange={this.handleChange('form.addressSportsEquipmentDelayInfo')}
                  value={addressSportsEquipmentDelayInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.address}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        <div className={'row'}>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="contactForBaggageInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={contactSportsEquipmentDelayInfo}
                fieldName={messages.contact}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="contactForBaggageInfo"
                  type="text"
                  onChange={this.handleChange('form.contactSportsEquipmentDelayInfo')}
                  value={contactSportsEquipmentDelayInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.contact}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="phoneForBaggageInfo"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={phoneSportsEquipmentDelayInfo}
                fieldName={messages.phone}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="phoneForBaggageInfo"
                  type="text"
                  onChange={this.handleChange('form.phoneSportsEquipmentDelayInfo')}
                  value={phoneSportsEquipmentDelayInfo}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.phone}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        {this.renderExpenseItemsSportsEquipmentDelay()}
        {this.renderOtherInsuranceAuthorizationForSportsEquipmentDelay()}
      </div>
    );
  }

  renderTravelSupplierForSportsEquipmentDelay() {
    const { isSummary, submitted } = this.props;
    const {
      originationSportsEquipmentDelay,
      flightNumberOriginationSportsEquipmentDelay,
      airCarrierOriginationSportsEquipmentDelay,
      destinationSportsEquipmentDelay,
      flightNumberDestinationSportsEquipmentDelay,
      airCarrierDestinationSportsEquipmentDelay,
      nameOfTourOperatorOrCruiseOrLineOrAirlineSportsEquipmentDelay,
      scheduledDateOfDepartureSportsEquipmentDelay,
      scheduledDateOfReturnSportsEquipmentDelay,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div className="ml-2">
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.travelSupplierOrProviderInformation} />
          </PageSectionHeader>
        </div>
        <div className={'row'}>
            <div className="form-group col-sm-8">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={nameOfTourOperatorOrCruiseOrLineOrAirlineSportsEquipmentDelay}
                fieldName={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="nameOfTourOperatorOrCruiseOrLineOrAirlineForBaggage"
                  type="text"
                  onChange={this.handleChange('form.nameOfTourOperatorOrCruiseOrLineOrAirlineSportsEquipmentDelay')}
                  value={nameOfTourOperatorOrCruiseOrLineOrAirlineSportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.nameOfTourOperatorOrCruiseOrLineOrAirline}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
            </div>
        </div>
        <div className={'row'}>
          <div className="form-group col-md-6">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="scheduledDateOfDeparture"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={scheduledDateOfDepartureSportsEquipmentDelay}
                fieldName={messages.scheduledDateOfDeparture}
              >
                <DatePicker
                  position={'bottom-start'}
                  disabled={isReadOnly}
                  name="scheduledDateOfDeparture"
                  type="text"
                  onDayChange={this.handleDayChange('form.scheduledDateOfDepartureSportsEquipmentDelay')}
                  value={scheduledDateOfDepartureSportsEquipmentDelay}
                  placeholder=""
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.scheduledDateOfDeparture}
                  />
                </ErrorMessage>
              </Validate>
          </div>
          <div className="form-group col-md-6">
            <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="scheduledDateOfReturn"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={scheduledDateOfReturnSportsEquipmentDelay}
                fieldName={messages.scheduledDateOfReturn}
              >
                <DatePicker
                  position={'bottom-start'}
                  disabled={isReadOnly}
                  name="scheduledDateOfReturn"
                  type="text"
                  onDayChange={this.handleDayChange('form.scheduledDateOfReturnSportsEquipmentDelay')}
                  value={scheduledDateOfReturnSportsEquipmentDelay}
                  placeholder=""
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.scheduledDateOfReturn}
                  />
                </ErrorMessage>
              </Validate>
            </div>
          </div>
        <div className="row">
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="origination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={originationSportsEquipmentDelay}
                fieldName={messages.origination}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="origination"
                  type="text"
                  onChange={this.handleChange('form.originationSportsEquipmentDelay')}
                  value={originationSportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.origination}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="flightNumberOrigination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={flightNumberOriginationSportsEquipmentDelay}
                fieldName={messages.flightNumber}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="flightNumberOrigination"
                  type="text"
                  onChange={this.handleChange('form.flightNumberOriginationSportsEquipmentDelay')}
                  value={flightNumberOriginationSportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.flightNumber}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="airCarrierOrigination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={airCarrierOriginationSportsEquipmentDelay}
                fieldName={messages.airCarrier}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="airCarrierOrigination"
                  type="text"
                  onChange={this.handleChange('form.airCarrierOriginationSportsEquipmentDelay')}
                  value={airCarrierOriginationSportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.airCarrier}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        <div className="row">
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="destination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={destinationSportsEquipmentDelay}
                fieldName={messages.destination}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="destination"
                  type="text"
                  onChange={this.handleChange('form.destinationSportsEquipmentDelay')}
                  value={destinationSportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.destination}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="flightNumberDestination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={flightNumberDestinationSportsEquipmentDelay}
                fieldName={messages.flightNumber}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="flightNumberDestination"
                  type="text"
                  onChange={this.handleChange('form.flightNumberDestinationSportsEquipmentDelay')}
                  value={flightNumberDestinationSportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.flightNumber}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-4">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="airCarrierDestination"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={airCarrierDestinationSportsEquipmentDelay}
                fieldName={messages.airCarrier}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="airCarrierDestination"
                  type="text"
                  onChange={this.handleChange('form.airCarrierDestinationSportsEquipmentDelay')}
                  value={airCarrierDestinationSportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.airCarrier}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
    </div>
    );
  }

  renderExpenseItemsSportsEquipmentDelay() {
    const { expenseItemsSportsEquipmentDelay } = this.props.form;
    const newAdditionalItems = this.appendIds(expenseItemsSportsEquipmentDelay);
    const { isSummary, submitted } = this.props;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <PageSectionHeader>
          <FormattedMessage {...messages.additionalItem} />
        </PageSectionHeader>
        <AdditionalList
          data={newAdditionalItems}
          onChange={this.onSportsEquipmentDelayExpensesChange}
          isDelayed={false}
          disabled={isReadOnly}
          validatorGroup={this.validatorGroup}
          validationCounter={this.state.validationCounter}
        />
      </div>
    );
  }

  renderOtherInsuranceAuthorizationForSportsEquipmentDelay() {
    const { isSummary, submitted } = this.props;
    const {
      companyNameSportsEquipmentDelay,
      addressSportsEquipmentDelay,
      typeOfPolicySportsEquipmentDelay,
      policySportsEquipmentDelay,
      contactSportsEquipmentDelay,
      phoneSportsEquipmentDelay,
    } = isSummary ? this.summaryFormValue() : this.props.form;
    const isReadOnly = submitted || isSummary;

    return (
      <div>
        <div className="row">
          <PageSectionHeader>
            <FormattedMessage {...messages.otherInsuranceAuthorization} />
          </PageSectionHeader>
        </div>
        <div className="row">
            <div className="form-group col-sm-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="companyNameForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={companyNameSportsEquipmentDelay}
                fieldName={messages.companyName}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="companyNameForBaggage"
                  type="text"
                  onChange={this.handleChange('form.companyNameSportsEquipmentDelay')}
                  value={companyNameSportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.companyName}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-12">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="addressForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={addressSportsEquipmentDelay}
                fieldName={messages.address}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="companyNameForBaggage"
                  type="text"
                  onChange={this.handleChange('form.addressSportsEquipmentDelay')}
                  value={addressSportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.address}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        <div className="row">
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="typeOfPolicyForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={typeOfPolicySportsEquipmentDelay}
                fieldName={messages.typeOfPolicy}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="typeOfPolicyForBaggage"
                  type="text"
                  onChange={this.handleChange('form.typeOfPolicySportsEquipmentDelay')}
                  value={typeOfPolicySportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.typeOfPolicy}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="policyForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={policySportsEquipmentDelay}
                fieldName={messages.policy}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="policyForBaggage"
                  type="text"
                  onChange={this.handleChange('form.policySportsEquipmentDelay')}
                  value={policySportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.policy}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
        <div className="row">
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="contactForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={contactSportsEquipmentDelay}
                fieldName={messages.contact}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="contactForBaggage"
                  type="text"
                  onChange={this.handleChange('form.contactSportsEquipmentDelay')}
                  value={contactSportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.contact}
                  />
                </ErrorMessage>
              </Validate>
            </div>
            <div className="form-group col-sm-6">
              <Validate
                validatorLeaveGroup={this.validatorGroup.removeValidatorToGroup}
                name="phoneForBaggage"
                group={this.validatorGroup}
                validators={[required]}
                addValidatorToGroup={this.validatorGroup.addValidatorToGroup}
                validChangeInGroup={this.validatorGroup.validChangeInGroup}
                validationCounter={this.state.validationCounter}
                defaultValue={phoneSportsEquipmentDelay}
                fieldName={messages.phone}
              >
                <Textbox
                  disabled={isReadOnly}
                  name="phoneForBaggage"
                  type="text"
                  onChange={this.handleChange('form.phoneSportsEquipmentDelay')}
                  value={phoneSportsEquipmentDelay}
                />
                <ErrorMessage for="required">
                  <FormattedMessage
                    {...messages.isRequired}
                    values={messages.phone}
                  />
                </ErrorMessage>
              </Validate>
            </div>
        </div>
    </div>
    );
  }

  render() {
    const {
      areYouClaimingForLostSportsEquipmentRental,
      areYouClaimingForLostSportsEquipment,
      areYouClaimingForSportsEquipmentDelay,
    } = this.props.form;
    const { isSummary, submitted, loading } = this.props;

    const isReadOnly = submitted || isSummary;
    const isRenderClaimingForLostSportsEquipmentRental = !!stepSportsEquipmentLookup.areYouClaimingForLostSportsEquipmentRental.claimCode;
    // const isRenderClaimingLostSportsEquipment = true;
    // const isRenderClaimingForSportsEquipmentDelay = true;
    return (
      <div className="app-content">
        {isRenderClaimingForLostSportsEquipmentRental &&
        <div className="row">
          <div className="form-group col">
            <PageSectionHeader>
              <FormattedMessage
                {...messages.areYouClaimingForLostSportsEquipmentRental}
              />
            </PageSectionHeader>
            <FlexBox>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  checked={
                    areYouClaimingForLostSportsEquipmentRental ===
                    'true'
                  }
                  onChange={this.onChange(
                    'form.areYouClaimingForLostSportsEquipmentRental'
                  )}
                  value="true"
                />
                <FormattedMessage {...messages.yes} />
              </OptionsContainer>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  checked={
                    areYouClaimingForLostSportsEquipmentRental ===
                    'false'
                  }
                  onChange={this.onChange(
                    'form.areYouClaimingForLostSportsEquipmentRental'
                  )}
                  // onClick={this.props.onResetDamagedLostExpense}
                  value="false"
                />
                <FormattedMessage {...messages.no} />
              </OptionsContainer>
            </FlexBox>
          </div>
        </div>
        }
        {isRenderClaimingForLostSportsEquipmentRental && this.renderClaimingForLostSportsEquipmentRental()}

        {isRenderClaimingForLostSportsEquipmentRental &&
        <div className="row">
          <div className="form-group col">
            <PageSectionHeader>
              <FormattedMessage
                {...messages.areYouClaimingForLostSportsEquipment}
              />
            </PageSectionHeader>
            <FlexBox>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  checked={
                    areYouClaimingForLostSportsEquipment ===
                    'true'
                  }
                  onChange={this.onChange(
                    'form.areYouClaimingForLostSportsEquipment'
                  )}
                  value="true"
                />
                <FormattedMessage {...messages.yes} />
              </OptionsContainer>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  checked={
                    areYouClaimingForLostSportsEquipment ===
                    'false'
                  }
                  onChange={this.onChange(
                    'form.areYouClaimingForLostSportsEquipment'
                  )}
                  // onClick={this.props.onResetDamagedLostExpense}
                  value="false"
                />
                <FormattedMessage {...messages.no} />
              </OptionsContainer>
            </FlexBox>
          </div>
        </div>
        }
        {isRenderClaimingForLostSportsEquipmentRental && this.renderClaimingForLostSportsEquipment()}

        {isRenderClaimingForLostSportsEquipmentRental &&
        <div className="row">
          <div className="form-group col">
            <PageSectionHeader>
              <FormattedMessage
                {...messages.areYouClaimingForSportsEquipmentDelay}
              />
            </PageSectionHeader>
            <FlexBox>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  checked={
                    areYouClaimingForSportsEquipmentDelay ===
                    'true'
                  }
                  onChange={this.onChange(
                    'form.areYouClaimingForSportsEquipmentDelay'
                  )}
                  value="true"
                />
                <FormattedMessage {...messages.yes} />
              </OptionsContainer>
              <OptionsContainer>
                <input
                  disabled={isReadOnly}
                  type="radio"
                  checked={
                    areYouClaimingForSportsEquipmentDelay ===
                    'false'
                  }
                  onChange={this.onChange(
                    'form.areYouClaimingForSportsEquipmentDelay'
                  )}
                  // onClick={this.props.onResetDamagedLostExpense}
                  value="false"
                />
                <FormattedMessage {...messages.no} />
              </OptionsContainer>
            </FlexBox>
          </div>
        </div>
        }
        {isRenderClaimingForLostSportsEquipmentRental && this.renderClaimingForSportsEquipmentDelay()}

        {!isSummary && (
          <div className={'row justify-content-between'}>
            <div className={'col-12 col-sm-auto'}>
              <div className={'row'}>
                <div className={'col-12 col-sm-auto'}>
                  <button
                    className="btn-outline"
                    onClick={this.props.prev}
                    onKeyDown={this.handleKeyDown}
                  >
                    Back
                  </button>
                </div>
                <div className={'col-12 col-sm-auto'}>
                  <Button
                    onClick={this.handleNext}
                    onKeyDown={this.handleKeyDown}
                    innerRef={(ref) => {
                      this.nextButton = ref;
                    }}
                  >
                    Next &gt;
                  </Button>
                </div>
              </div>
            </div>
            <div className={'col-12 col-sm-auto'}>
              {!submitted && (
                <AsyncButton
                  id={this.saveButtonId}
                  className="btn-outline"
                  loading={loading}
                  spinner={this.showSpinnerForElement(this.saveButtonId)}
                  onClick={this.handleSaveButtonClick}
                  onKeyDown={this.handleKeyDown}
                >
                  Save
                </AsyncButton>
              )}
            </div>
          </div>
        )}
        <ReactResizeDetector handleHeight onResize={this.onResize} />
      </div>
    );
  }
}

SportsEquipmentStep.propTypes = {
  form: PropTypes.object,
  onFormDataChange: PropTypes.func,
  prev: PropTypes.func,
  next: PropTypes.func,
  onFormDataSave: PropTypes.func,
  onResetDelayedExpense: PropTypes.func,
  onResetDamagedLostExpense: PropTypes.func,
  loading: PropTypes.bool,
  isSummary: PropTypes.bool,
  submitted: PropTypes.bool,
  adjust: PropTypes.func,
  makeValid: PropTypes.func,
  makeError: PropTypes.func,
  onNext: PropTypes.func,
  step: PropTypes.number,
  currentStep: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isReadOnly: PropTypes.bool,
  errors: PropTypes.any,
  onResetCompensationExpense: PropTypes.func,
  validateStep: PropTypes.string,
  stepName: PropTypes.string,
};

const mapStateToProps = createStructuredSelector({
  form: makeSelectSportsEquipmentStep(),
  errors: makeSelectSportsEquipmentStepErrors(),
  currentStep: makeSelectCurrentStep(),
  loading: makeSelectProgressBarStatus(),
});

const mapDispatchToProps = (dispatch) => ({
  onFormDataChange: (propertyName, value) =>
    dispatch(handleFormDataChange(propertyName, value)),
  onFormDataSave: () => dispatch(saveFormData()),
  onNext: (errors) => dispatch(nextSlide(errors)),
  onResetDelayedExpense: () => dispatch(resetDelayedExpenseFormData()),
  onResetDamagedLostExpense: () => dispatch(resetDamagedLostExpenseFormData()),
  onResetCompensationExpense: () => dispatch(resetFormCompensationExpense()),
});

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps
);
const withReducer = injectReducer({ key: 'sportsEquipmentStep', reducer });

export default compose(
  withReducer,
  withConnect
)(SportsEquipmentStep);



// WEBPACK FOOTER //
// ./app/containers/FastCover/Trawick.NationWide.SportsEquipmentStep/index.js